import React from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './Copilot.scss';

interface CopilotMarkdownProps {
  content: string;
}

/**
 * Open links in a new tab so clicking an entity deep-link (audit, task, question, etc.)
 * doesn't navigate away from — and discard — the conversation. `rel="noopener noreferrer"`
 * is required with `target="_blank"` to prevent the opened page from accessing `window.opener`
 * (reverse tabnabbing) and to drop the referrer. react-markdown already sanitizes the href
 * (relative and http/https/mailto/tel kept; javascript: and other schemes dropped), so we
 * only add link behavior here — no raw HTML is ever rendered.
 */
const markdownComponents: Components = {
  // Only forward the anchor attributes we actually need (href, title). This avoids
  // spreading react-markdown's `node` (hast AST) prop onto the native <a> element,
  // which would trigger a React unknown-DOM-prop warning.
  a: ({ children, href, title }) => (
    <a href={href} title={title} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
};

/**
 * Renders an agent answer written in markdown.
 *
 * Uses `react-markdown`, which parses markdown into a React element tree — there is no
 * `dangerouslySetInnerHTML` and no HTML-string injection. It is safe by default: raw HTML
 * embedded in the markdown is not rendered as markup (no `rehype-raw` plugin), so a model
 * cannot emit active content. `remark-gfm` adds GitHub-flavored markdown (tables, task
 * lists, strikethrough, autolinks). Links (e.g. entity deep-links the agent emits as
 * relative paths, which resolve to the current stage's origin) open in a new tab.
 */
export function CopilotMarkdown({ content }: CopilotMarkdownProps) {
  return (
    <div className="copilot-markdown">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {content}
      </ReactMarkdown>
    </div>
  );
}
